import { AppConfig } from './../app.config';
import { AwsUtil } from './../authentication/aws.service';
import { DashboardMetricBarConfigurator } from './../dashboard/metric/dashboard-metric-bar-configurator';
import {
    UserProfile,
    UserProfileRiskManager,
    DashboardMetricCategory,
    DashboardMetricValue,
    WebApiEndpoints,
} from '@liquitics/transaction';
import { MetricTile } from './../metrics/metric-tile';
import { convertToDashboardMetricSets } from './risk-graphical/risk-graphical-config';
import { DashboardMetricSet } from './../dashboard/dashboard-metric';
import * as moment from 'moment';
import { CurrencyDisplayUtil } from '../common/currency-display-util';


export interface RiskDashboardMetricData {
    type: string;
    calculation: string;
    highlight: boolean;
    datetime: string;
    currency: string;
    amount: number;
    scenario?: string;
}

export abstract class BaseDashboardMetricBarConfigurator<T>  {

    constructor(protected maxColumnsPerRow: number) {

    }

    protected rowCount(itemCount: number): number {
        let approxRows = itemCount / this.maxColumnsPerRow;
        let rows = Math.floor(approxRows);
        if (((rows * this.maxColumnsPerRow) % 2) !== (itemCount) && itemCount < this.maxColumnsPerRow) {
            itemCount++;
            approxRows = itemCount / this.maxColumnsPerRow;
        }
        if (approxRows > rows) {
            rows += 1;
        }
        return rows;
    }

    protected columnCount(itemCount: number): number {
        const rows = this.rowCount(itemCount);
        const fairCols = itemCount / rows;
        let cols = Math.floor(fairCols);
        if (fairCols > cols) {
            cols += 1;
        }
        return cols;
    }

    initTiles(dashboardMetricSetData: DashboardMetricSet[]): MetricTile<DashboardMetricSet>[] {
        const tiles: MetricTile<DashboardMetricSet>[] = [];
        if (dashboardMetricSetData.length > 0) {
            dashboardMetricSetData.forEach((dashboardMetricSet: DashboardMetricSet) => {
                // if (!(dashboardMetricSet.subType) || (dashboardMetricSet.subType !== 'All')) {
                tiles.push({
                    type: 'summary',
                    cols: 1,
                    rows: 1,
                    metric: dashboardMetricSet,
                    fxFlex: '4 1 calc(25%-10px)',
                } as MetricTile<DashboardMetricSet>
                );
                // };
            })
        }
        return tiles;
    }

    blankTiles(riskMetricTiles: MetricTile<DashboardMetricSet>[]): any[] {
        const blankTiles = [];
        const numTiles = riskMetricTiles.length;
        const perRow = this.maxColumnsPerRow;
        const modulus = numTiles % perRow;
        const blanksNeeded = modulus === 0 ? 0 : (perRow - modulus);
        for (let i = 0; i < blanksNeeded; i++) {
            blankTiles.push('');
        }
        return blankTiles;
    }

    getMaxColumnsPerRow(): number {
        return this.maxColumnsPerRow;
    }
}

export class NormalRiskMetricTiles extends BaseDashboardMetricBarConfigurator<RiskDashboardMetricData>
    implements DashboardMetricBarConfigurator<RiskDashboardMetricData[]> {
    currency: string;
    stress: string;
    batchIdentifier: string;
    dashboardMetricCategory: DashboardMetricCategory;
    profile: UserProfileRiskManager;
    calculation: string;
    constructor() {
        super(4);
    }

    init(
        selectedCurrency: string,
        selectedSubType: string,
        selectedBatchIdentifier: string,
        selectedDashboardMetricCategory: DashboardMetricCategory,
        profile: UserProfile,
        selectedCalculation: string
    ) {
        this.currency = selectedCurrency;
        this.stress = selectedSubType;
        this.batchIdentifier = selectedBatchIdentifier;
        this.dashboardMetricCategory = selectedDashboardMetricCategory;
        this.profile = profile as UserProfileRiskManager;
        this.calculation = selectedCalculation;
    }

    private findMetricValue(metricValues: DashboardMetricValue[], type: string, currency: string,
        calculation: string): DashboardMetricValue {
        return metricValues.find((metricValue: DashboardMetricValue) => {
            return metricValue.type === type && metricValue.currency === currency && metricValue.calculation === calculation;
        });
    }


    queryDashboardData(awsUtil: AwsUtil, appConfig: AppConfig): Promise<RiskDashboardMetricData[]>[] {
        // We return an array here, probably because of the typescript structure we needed when we refactored the original hack...
        // We later only grab the 0th element...
        return [awsUtil.webAPICall(WebApiEndpoints.QueryRiskMetricData, {})];
    }

    generateDashboardMetricsSets(riskMetricData: RiskDashboardMetricData[][]): DashboardMetricSet[] {
        const dashboardMetricSets: DashboardMetricSet[] = [];
        const metricValues = riskMetricData[0]; // See queryDashboardData for why we do this
        const isSecondary = (this.dashboardMetricCategory !== undefined);
        if (isSecondary) {
            this.calculation = '30D';
        }
        const categories = isSecondary ? [this.dashboardMetricCategory] : this.profile.getRiskMetricCategories();
        categories
            .forEach((dashboardMetricCategory: DashboardMetricCategory, categoryIndex: number) => {
                const metrics = isSecondary ? dashboardMetricCategory.secondaryMetrics : dashboardMetricCategory.primaryMetrics;
                const convertedSets: DashboardMetricSet[] = convertToDashboardMetricSets(metrics);
                convertedSets.forEach((dashboardMetricSet: DashboardMetricSet, metricIndex: number) => {
                    dashboardMetricSet.icon = dashboardMetricCategory.icon;
                    dashboardMetricSet.fontSize = '3em';
                    dashboardMetricSet.category = dashboardMetricCategory;
                    dashboardMetricSet.categoryIndex = isSecondary ? metricIndex : categoryIndex;
                    dashboardMetricSet.values = [];
                    dashboardMetricSet.dates = [];
                    dashboardMetricSet.metrics.forEach((metric: string) => {
                        const dashboardMetricValue = this.findMetricValue(metricValues, metric,
                            this.currency, this.calculation);
                        if (dashboardMetricValue) {
                            dashboardMetricSet.values.push(CurrencyDisplayUtil.toShortNumber(dashboardMetricValue.amount, 2));
                            dashboardMetricSet.dates.push(moment(dashboardMetricValue.datetime).format('L'));
                        } else {
                            dashboardMetricSet.values.push(undefined);
                            dashboardMetricSet.dates.push('');
                        }
                    });
                    dashboardMetricSets.push(dashboardMetricSet);
                })
            })
        return dashboardMetricSets;
    }
    initLabelTiles(dashboardMetricSetData: DashboardMetricSet[]): MetricTile<DashboardMetricSet>[] {
        const labelTiles: MetricTile<DashboardMetricSet>[] = [];
        return labelTiles;
    }
}

export class StressRiskMetricTiles extends BaseDashboardMetricBarConfigurator<RiskDashboardMetricData>
    implements DashboardMetricBarConfigurator<RiskDashboardMetricData[]> {
    currency: string;
    stress: string;
    batchIdentifier: string;
    dashboardMetricCategory: DashboardMetricCategory;
    profile: UserProfileRiskManager;
    calculation: string;
    constructor() {
        super(6);
    }

    init(
        selectedCurrency: string,
        selectedSubType: string,
        selectedBatchIdentifier: string,
        selectedDashboardMetricCategory: DashboardMetricCategory,
        profile: UserProfile,
        selectedCalculation: string
    ) {
        this.currency = selectedCurrency;
        this.stress = selectedSubType;
        this.batchIdentifier = selectedBatchIdentifier;
        this.dashboardMetricCategory = selectedDashboardMetricCategory;
        this.profile = profile as UserProfileRiskManager;
        this.calculation = selectedCalculation;
    }

    private addMetricRow(multipliedMetricSet: DashboardMetricSet[], scenarioLabel: string, row?: DashboardMetricSet, category?: DashboardMetricCategory) {
        if (!row) {
            multipliedMetricSet.push(
                {
                    type: 'Single',
                    initialized: true,
                    icon: null,
                    category: category,
                    categoryIndex: null,
                    name: scenarioLabel,
                    label: scenarioLabel,
                    metrics: [],
                    values: [' '],
                    dates: [],
                    subType: 'None'
                });
        } else {
            multipliedMetricSet.push(
                {
                    type: row.type,
                    initialized: row.initialized,
                    icon: row.icon,
                    category: row.category,
                    categoryIndex: row.categoryIndex,
                    name: row.name,
                    label: row.label,
                    metrics: row.metrics,
                    values: row.values,
                    dates: row.dates,
                    subType: scenarioLabel,
                    fontSize: '3em',
                });
        }
        return multipliedMetricSet;
    }

    private addLabelMetrics(multipliedMetrics: any[], dashboardMetricCategory: DashboardMetricCategory): DashboardMetricSet[] {
        this.addMetricRow(multipliedMetrics, 'Type');
        this.addMetricRow(multipliedMetrics, 'Normal');
        this.addMetricRow(multipliedMetrics, 'Scenario 1');
        this.addMetricRow(multipliedMetrics, 'Scenario 2');
        this.addMetricRow(multipliedMetrics, 'Scenario 3');
        this.addMetricRow(multipliedMetrics, 'Scenario 4');
        this.addMetricRow(multipliedMetrics, 'Scenario 5');
        return multipliedMetrics;
    };

    private multiplyMetricSet(metricSet: any[]): DashboardMetricSet[] {
        const multipliedMetrics = [];

        metricSet.forEach((row, index: number) => {
            this.addMetricRow(multipliedMetrics, 'All', row);
            this.addMetricRow(multipliedMetrics, 'normal', row);
            this.addMetricRow(multipliedMetrics, 'scenario1', row);
            this.addMetricRow(multipliedMetrics, 'scenario2', row);
            this.addMetricRow(multipliedMetrics, 'scenario3', row);
            this.addMetricRow(multipliedMetrics, 'scenario4', row);
            this.addMetricRow(multipliedMetrics, 'scenario5', row);
        });
        return multipliedMetrics;
    }

    private findMetricValue(metricValues: RiskDashboardMetricData[], type: string, currency: string,
        calculation: string, scenario: string): RiskDashboardMetricData {
        return metricValues.find((metricValue: RiskDashboardMetricData) => {
            return metricValue.type === type && metricValue.currency === currency && metricValue.calculation === calculation
                && metricValue.scenario === scenario;
        })
    }

    queryDashboardData(awsUtil: AwsUtil, appConfig: AppConfig): Promise<RiskDashboardMetricData[]>[] {
        // We return an array here, probably because of the typescript structure we needed when we refactored the original hack...
        // We later only grab the 0th element...
        return [awsUtil.webAPICall(WebApiEndpoints.QueryStressedRiskMetricData, {})];
    }

    generateDashboardMetricsSets(riskMetricData: RiskDashboardMetricData[][]): DashboardMetricSet[] {
        const metricValues = riskMetricData[0]; // See queryDashboardData for why we do this
        const dashboardMetricSets = [];
        const isSecondary = (this.dashboardMetricCategory !== undefined);
        if (isSecondary) {
            this.calculation = '30D';
        } else {
            this.calculation = 'YESTERDAY';
        }
        const categories = isSecondary ? [this.dashboardMetricCategory] : this.profile.getRiskMetricCategories();
        categories.filter((row) => {
            return row['intradayStress'];
        })
            .forEach((dashboardMetricCategory: DashboardMetricCategory, categoryIndex: number) => {
                const metrics = isSecondary ? dashboardMetricCategory.secondaryMetrics : dashboardMetricCategory.primaryMetrics;
                const convertedSets = convertToDashboardMetricSets(metrics);
                let stressedMetricSets = this.multiplyMetricSet(convertedSets);
                if ((categoryIndex === 0) && (!this.dashboardMetricCategory)) {
                    stressedMetricSets = this.addLabelMetrics(stressedMetricSets, dashboardMetricCategory);
                }
                stressedMetricSets.filter((row) => {
                    if (this.stress) {
                        return !row.label.includes('Theoretical') && row.subType === this.stress;
                    } else {
                        return !row.label.includes('Theoretical'); // Theoretical is not supported for intraday stress
                    }
                })
                    .forEach((dashboardMetricSet: DashboardMetricSet, metricIndex: number) => {
                        dashboardMetricSet.icon = dashboardMetricCategory.icon;
                        dashboardMetricSet.fontSize = '1.5em';
                        dashboardMetricSet.category = dashboardMetricCategory;
                        dashboardMetricSet.categoryIndex = isSecondary ? metricIndex : categoryIndex;
                        if ((dashboardMetricSet.subType !== 'All') && (dashboardMetricSet.subType !== 'None')) {
                            dashboardMetricSet.values = [];
                            dashboardMetricSet.dates = [];
                            if (dashboardMetricSet.type === 'Single') {
                                dashboardMetricSet.label = ' ';
                                dashboardMetricSet.icon = null;
                            } else {
                                dashboardMetricSet.icon = dashboardMetricCategory.icon;
                            }
                            dashboardMetricSet.metrics.forEach((metric: string) => {
                                const dashboardMetricValue = this.findMetricValue(metricValues, metric,
                                    this.currency, this.calculation, dashboardMetricSet.subType);
                                if (dashboardMetricValue) {
                                    dashboardMetricSet.values.push(CurrencyDisplayUtil.toShortNumber(dashboardMetricValue.amount, 2));
                                    dashboardMetricSet.dates.push(moment(dashboardMetricValue.datetime).format('L'));
                                } else {
                                    dashboardMetricSet.values.push(undefined);
                                    dashboardMetricSet.dates.push('');
                                }
                            });
                        } else {
                            dashboardMetricSet.values = [];
                            dashboardMetricSet.dates = [];
                            if (dashboardMetricSet.subType === 'None') {
                                dashboardMetricSet.icon = null;
                            }
                            dashboardMetricSet.dates.push('');
                            dashboardMetricSet.values.push(' ');
                        }
                        dashboardMetricSets.push(dashboardMetricSet);
                    })
            })
        return dashboardMetricSets;
    }

    initLabelTiles(dashboardMetricSetData: DashboardMetricSet[]): MetricTile<DashboardMetricSet>[] {
        const labelTiles: MetricTile<DashboardMetricSet>[] = [];
        const riskLabelMetrics = dashboardMetricSetData.filter((row) => {
            return (row.subType === 'None');
        });
        riskLabelMetrics.forEach((dashboardMetricSet: DashboardMetricSet, index: number) => {
            if (!((dashboardMetricSet.type === 'Top3') && (dashboardMetricSet.subType === 'All'))) {
                labelTiles.push({
                    type: 'risk-summary',
                    cols: 1,
                    rows: 1,
                    metric: dashboardMetricSet,
                } as MetricTile<DashboardMetricSet>
                );
            }
        })
        return labelTiles;
    }

    initTiles(dashboardMetricSetData: DashboardMetricSet[]): MetricTile<DashboardMetricSet>[] {
        const tiles: MetricTile<DashboardMetricSet>[] = super.initTiles(dashboardMetricSetData);
        return tiles
            .map((tile: MetricTile<DashboardMetricSet>) => {
                if (tile.metric.subType === 'All') {
                    tile.fxFlex = '1 1 calc(22%-10px)';
                } else {
                    tile.fxFlex = '6 1 calc(13%-10px)';
                }
                return tile;
            })
            .filter((tile: MetricTile<DashboardMetricSet>) => {
                return tile.metric.subType !== 'None';
            });
    }

}



